import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Col, Row, Space } from 'antd'
import { PrimaryButton } from 'Components/button'
import { isFormRequired, isFormError, formValidation } from 'helpers/format'
import { selectCurrentUser } from 'Store/user/selector'
import { AppInput, AppTextArea } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/tax-management/actions'
import LoadingView from 'Components/modal/LoadingView'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { getMerchantTaxAPI, updateMerchantTaxAPI } from 'Store/tax-management/api'
import { UpdateMerchantTaxReq } from 'Store/tax-management/types'
import {
  selectMerchantTaxData,
  selectMerchantTaxEvent,
  selectUpdateMerchantTaxEvent,
} from 'Store/tax-management/selector'

type Props = ReduxProps & {}

const MerchantInfo: React.FunctionComponent<Props> = props => {
  const { currentUser, event, detail, updateEvent } = props
  const requireFeild = ['address', 'mobile', 'name', 'tax_invoice_no']
  const initForm = {
    address: '',
    mobile: '',
    name: '',
    tax_invoice_no: '',
  } as UpdateMerchantTaxReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const dispatch = useDispatch()

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const onGetDetail = (): void => {
    dispatch(getMerchantTaxAPI({}, currentUser.accessToken, currentUser?.activeMerchant?.id || 0))
  }

  const onUpdate = (): void => {
    if (!validateForm()) return
    const req: UpdateMerchantTaxReq = {
      ...formData,
    }
    dispatch(updateMerchantTaxAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.id || 0))
  }

  useEffect(() => {
    onGetDetail()
  }, [])

  useEffect(() => {
    if (detail) {
      const form = {
        ...detail,
      } as UpdateMerchantTaxReq
      setFormData({ ...form })
    }
  }, [detail])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const onUpdateData = (val: string, key: string) => {
    if (key === 'tax_invoice_no' && val.length > 13) {
      return
    }
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onGetDetail()
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  return (
    <PageWrapper>
      <Title>ข้อมูลร้านค้าสำหรับออกใบกำกับภาษี</Title>
      <LoadingView isShow={event.IsLoading} />
      <EventModal
        event={[updateEvent]}
        enable={true}
        successCalback={onEventSuccess}
        errorCalback={clearEvent}
      />
      <Row justify="space-between" gutter={16}>
        <Col md={12} xs={24}>
          <AppInput
            name="name"
            label="ชื่อจดทะเบียนบริษัท"
            isRequired={isRequired('name')}
            isError={isError('name')}
            value={formData.name}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="tax_invoice_no"
            label="เลขประจำตัวผู้เสียภาษี"
            isRequired={isRequired('tax_invoice_no')}
            isError={isError('tax_invoice_no')}
            value={formData.tax_invoice_no}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="mobile"
            label="เบอร์โทรศัพท์"
            isRequired={isRequired('mobile')}
            isError={isError('mobile')}
            value={formData.mobile}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppTextArea
            label="ที่อยู่จดทะเบียนบริษัท"
            isRequired={isRequired('address')}
            isError={isError('address')}
            name="address"
            rows={3}
            value={formData.address}
            onChange={onUpdateData}
          />
        </Col>

        <Col md={24} xs={24}>
          <Space size="middle" align="baseline">
            <PrimaryButton text="บันทึกข้อมูล" onClick={onUpdate} />
          </Space>
        </Col>
      </Row>
    </PageWrapper>
  )
}

const PageWrapper = styled.div`
  background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.white};
  padding: 20px;
`

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.medium};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  margin-bottom: 10px;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectMerchantTaxEvent(state),
    detail: selectMerchantTaxData(state),
    updateEvent: selectUpdateMerchantTaxEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(MerchantInfo)
